import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Form, FormGroup } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { activityTypes } from 'app/frontend/types/custom/ActivityType'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { eventSelectionStatuses } from 'app/frontend/types/custom/EventSelectionStatus'
import Card from '../../../../components/Card'
import TimeField from '../../../../components/form-fields/TimeField'
import SelectField from '../../../../components/form-fields/SelectField'
import { FormEntityContext } from '../../../../config/context/forms'
import { useTeam } from '../../../../config/context/team'
import { updateTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import AddEventDialog, {
  AddEventFormValues,
} from '../../../TeamDashboard/subpages/TeamEventOverview/components/AddEventDialog'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'

const WEEK_DAYS = [
  { value: '1', short: 'Mon', label: 'Monday' },
  { value: '2', short: 'Tue', label: 'Tuesday' },
  { value: '3', short: 'Wed', label: 'Wednesday' },
  { value: '4', short: 'Thu', label: 'Thursday' },
  { value: '5', short: 'Fri', label: 'Friday' },
  { value: '6', short: 'Sat', label: 'Saturday' },
  { value: '0', short: 'Sun', label: 'Sunday' },
] as const

const dayConfigSchema = z.object({
  id: z.number().optional(),
  weekDay: z.string().regex(/^[0-6]$/),
  enabled: z.boolean(),
  startTime: z.string().min(1),
  finishTime: z.string().min(1),
  optional: z.boolean(),
  activityType: z.enum(activityTypes),
  instanceId: z.string().or(z.number()).nullable(),
  difficulty: z.enum(difficulties),
  title: z.string().nullable().optional(),
  autoAddParticipants: z.boolean(),
  lockHoursBeforeStart: z.number().or(z.string()).nullable(),
  lockAtPresentCount: z.number().or(z.string()).nullable(),
  defaultSignupStatus: z.enum(eventParticipantStatuses).nullable(),
  defaultSelectionStatus: z.enum(eventSelectionStatuses).nullable(),
  guestPolicy: z.string().nullable(),
})

const formSchema = z.object({
  days: z.array(dayConfigSchema),
})

type EventsFormValues = z.infer<typeof formSchema>
type DayConfig = EventsFormValues['days'][number]

const buildDefaultDays = (team: wowaudit.Team): DayConfig[] => {
  const recurringEvents = team.recurringEvents ?? []

  return WEEK_DAYS.map(({ value }) => {
    const existing = recurringEvents.find(
      (event) => `${event.weekDay}` === value,
    )

    return {
      id: existing?.id,
      weekDay: value,
      enabled: !!existing,
      startTime: existing?.startTime ?? '20:00',
      finishTime: existing?.finishTime ?? '23:00',
      optional: existing?.optional ?? false,
      activityType: existing?.activityType ?? 'raids',
      instanceId: existing?.instanceId ?? null,
      difficulty: existing?.difficulty ?? 'mythic',
      title: existing?.title ?? null,
      autoAddParticipants: existing?.autoAddParticipants ?? true,
      lockHoursBeforeStart:
        existing?.lockHoursBeforeStart ??
        team.settings.raidsAutoLock.value ??
        null,
      lockAtPresentCount: existing?.lockAtPresentCount ?? null,
      defaultSignupStatus: existing?.defaultSignupStatus ?? null,
      defaultSelectionStatus: existing?.defaultSelectionStatus ?? null,
      guestPolicy: existing?.guestPolicy ?? null,
    }
  })
}

type RecurringEventAttributes =
  | (Omit<DayConfig, 'enabled'> & { repeatFrom: string; repeatUntil: null })
  | { id: number; _destroy: true }

const EventsStepForm = ({ goNext, goBack, isFirst }: StepProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const today = format(new Date(), 'yyyy-MM-dd')

  const form = useForm<EventsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { days: buildDefaultDays(team) },
  })

  const days = form.watch('days')

  const onSubmit = (values: EventsFormValues) => {
    setSubmitting(true)

    const recurringEventsAttributes =
      values.days.flatMap<RecurringEventAttributes>((day) => {
        if (day.enabled) {
          const { enabled: _enabled, ...event } = day
          return [{ ...event, repeatFrom: today, repeatUntil: null }]
        }
        if (day.id) return [{ id: day.id, _destroy: true }]
        return []
      })

    const payload = {
      recurring_events_attributes: recurringEventsAttributes,
    } as Partial<wowaudit.Team>

    updateTeam(team.id, payload)
      .then((updatedTeam) => {
        queryClient.setQueryData(['team', { path: team.path }], updatedTeam)
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <StepLayout
      icon={Calendar}
      title='Set up your raid nights'
      description='Select the days your team gets together. Events will then be created automatically on a rolling basis, up to 3 months in the future.'
    >
      <Form {...form}>
        <FormEntityContext.Provider
          value={{ entityType: 'event', i18nPrefix: 'forms.fields' }}
        >
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='flex flex-wrap justify-center gap-2'>
              {days.map((day, index) => (
                <Button
                  key={day.weekDay}
                  type='button'
                  variant={day.enabled ? 'default' : 'outline'}
                  className='grow'
                  onClick={() =>
                    form.setValue(`days.${index}.enabled`, !day.enabled, {
                      shouldDirty: true,
                    })
                  }
                >
                  {WEEK_DAYS[index].short}
                </Button>
              ))}
            </div>

            <div className='mt-4 flex flex-col gap-3'>
              {days.map((day, index) =>
                day.enabled ? (
                  <Card key={day.weekDay} className='space-y-3'>
                    <div className='flex items-center justify-between gap-2'>
                      <h4 className='font-semibold mb-0'>
                        {WEEK_DAYS[index].label}
                      </h4>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => setEditingIndex(index)}
                      >
                        Advanced
                      </Button>
                    </div>

                    <FormGroup>
                      <TimeField
                        name={`days.${index}.startTime`}
                        customLabel='Start time'
                        className='w-auto shrink-0'
                      />
                      <TimeField
                        name={`days.${index}.finishTime`}
                        customLabel='End time'
                        className='w-auto shrink-0'
                      />
                      <SelectField
                        name={`days.${index}.activityType`}
                        options={activityTypes}
                        layout='stacked'
                        className='grow basis-0'
                        customLabel='Activity'
                      />
                      {day.activityType === 'raids' ? (
                        <SelectField
                          name={`days.${index}.difficulty`}
                          options={difficulties}
                          layout='stacked'
                          className='w-44 shrink-0'
                          customLabel='Difficulty'
                        />
                      ) : null}
                    </FormGroup>
                  </Card>
                ) : null,
              )}
            </div>

            <StepFooter
              showBack={!isFirst}
              onBack={goBack}
              onContinue={form.handleSubmit(onSubmit)}
              continueLoading={submitting}
              onSkip={goNext}
            />
          </form>
        </FormEntityContext.Provider>
      </Form>

      <AddEventDialog
        open={editingIndex !== null}
        setOpen={(open) => {
          if (!open) setEditingIndex(null)
        }}
        hideDateField
        title='Advanced configuration'
        submitLabel={t('shared.continue')}
        defaultValues={
          editingIndex === null
            ? undefined
            : (days[editingIndex] as Partial<AddEventFormValues>)
        }
        onSubmit={(values) => {
          if (editingIndex === null) return
          const index = editingIndex
          form.setValue(`days.${index}.startTime`, values.startTime, {
            shouldDirty: true,
          })
          form.setValue(`days.${index}.finishTime`, values.finishTime, {
            shouldDirty: true,
          })
          form.setValue(`days.${index}.optional`, values.optional, {
            shouldDirty: true,
          })
          form.setValue(`days.${index}.activityType`, values.activityType, {
            shouldDirty: true,
          })
          form.setValue(`days.${index}.instanceId`, values.instanceId, {
            shouldDirty: true,
          })
          form.setValue(
            `days.${index}.difficulty`,
            values.difficulty ?? 'mythic',
            { shouldDirty: true },
          )
          form.setValue(
            `days.${index}.autoAddParticipants`,
            values.autoAddParticipants,
            { shouldDirty: true },
          )
          form.setValue(
            `days.${index}.lockHoursBeforeStart`,
            values.lockHoursBeforeStart,
            { shouldDirty: true },
          )
          form.setValue(
            `days.${index}.lockAtPresentCount`,
            values.lockAtPresentCount,
            { shouldDirty: true },
          )
          form.setValue(
            `days.${index}.defaultSignupStatus`,
            values.defaultSignupStatus,
            { shouldDirty: true },
          )
          form.setValue(
            `days.${index}.defaultSelectionStatus`,
            values.defaultSelectionStatus,
            { shouldDirty: true },
          )
          form.setValue(`days.${index}.guestPolicy`, values.guestPolicy, {
            shouldDirty: true,
          })
          form.setValue(`days.${index}.title`, values.title, {
            shouldDirty: true,
          })
        }}
      />
    </StepLayout>
  )
}

const EventsStep = (props: StepProps) => {
  return <EventsStepForm {...props} />
}

export default EventsStep
